import './ProductCard.less';
import useAppDispatch from '../../../hooks/useAppDispatch';
import { Product } from '../../../models/Product';
import useAppSelector from '../../../hooks/useAppSelector';
import ProductImageCarousel from '../image-carousel/ProductImageCarousel';
import ProductDiscountBadge from '../badges/ProductDiscountBadge';
import ProductPrice from '../ProductPrice';
import ProductCardButtons, {
    type ProductCardButtonsAction
} from './ProductCardButtons';
import { useCallback } from 'preact/hooks';
import ProductRating from '../ProductRating';
import { dispatchEvent } from '../../../services/EventService';
import ProductCardOverlay from './ProductCardOverlay';
import IconButton from '../../button/IconButton';
import { forwardRef } from 'preact/compat';
import { useTranslation } from '../../../hooks/useTranslation';
import ProductOutOfStockBadge from '../badges/ProductOutOfStockBadge';

export type ProductCardProps = {
    animationDelay: number;
    product: Product;
    hideActions?: boolean;
    hideAddToCart?: boolean;
    source: 'brilliant' | 'web';
    featureType:
        | 'product-mix-and-match'
        | 'product-pack'
        | 'product-search-new'
        | 'product-search-by-text'
        | 'product-search-similar'
        | 'product-search-combinations'
        | 'product-search-complementary-category'
        | 'product-search-similar-by-image'
        | 'product-search-combinations-by-image';
};

const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
    (
        {
            product,
            animationDelay,
            hideActions,
            hideAddToCart,
            source,
            featureType
        }: ProductCardProps,
        ref
    ) => {
        const { productId, name } = product;
        const dispatch = useAppDispatch();
        const { t } = useTranslation();
        const mode = useAppSelector(state => state.uiState.mode);
        const discountBadge = useAppSelector(
            state => state.uiState.discountBadge
        );
        const discountedPercentage =
            (discountBadge !== 'hidden' &&
                product.available &&
                product.discountedPercentage) ||
            undefined;
        const selectedForQuestionProductId = useAppSelector(state =>
            state.featureState.mainFeatureItem?.featureType === 'product-detail'
                ? state.featureState.mainFeatureItem.productId
                : null
        );
        const selectedForComparisonProductIds = useAppSelector(
            state => state.productComparisonState.selectedProductIds
        );
        const selectedForAddingToCartProductIds = useAppSelector(
            state => state.cartState.addingProductIds
        );
        const onAction = useCallback(
            (action: ProductCardButtonsAction | 'add-to-cart') => {
                dispatchEvent({
                    type: 'bri.click_product_card_action',
                    isInteraction: true,
                    ...(source === 'brilliant'
                        ? { source, brilliantSource: 'ui' }
                        : { source }),
                    action,
                    productId
                });
                switch (action) {
                    case 'compare':
                        return dispatch.productComparisonState.toggleProductSelection(
                            productId
                        );
                    case 'product-search-similar':
                        return dispatch.featureState.startFeature({
                            featureType: 'product-search-similar',
                            baseProductsIds: [productId]
                        });
                    case 'product-search-combinations':
                        return dispatch.featureState.startFeature({
                            featureType: 'product-search-combinations',
                            baseProductsIds: [productId]
                        });
                    case 'product-mix-and-match':
                        return dispatch.featureState.startFeature({
                            featureType: 'product-mix-and-match',
                            productId
                        });
                    case 'product-detail':
                    case 'add-to-cart':
                        return dispatch.featureState.startFeature({
                            featureType: 'product-detail',
                            productId,
                            product,
                            source
                        });
                }
            },
            [dispatch, productId, product]
        );
        const isAssistantOpen = mode === 'full';
        let selectedOverlayText = '';
        if (
            selectedForAddingToCartProductIds.includes(productId) &&
            isAssistantOpen
        ) {
            selectedOverlayText = t('PRODUCT_CARD_OVERLAY_ADDING');
        } else if (
            selectedForQuestionProductId === productId &&
            isAssistantOpen
        ) {
            selectedOverlayText = t('PRODUCT_CARD_OVERLAY_ASKING');
        } else if (selectedForComparisonProductIds.includes(productId)) {
            selectedOverlayText = t('PRODUCT_CARD_OVERLAY_COMPARING');
        }
        const isSelected = selectedOverlayText !== '';
        const isSelectedForComparison =
            selectedForComparisonProductIds.includes(productId);
        const isComparingMode = selectedForComparisonProductIds.length > 0;
        const isComparisonFull = selectedForComparisonProductIds.length >= 3;
        return (
            <div
                ref={ref}
                data-bri-product-id={productId}
                style={{ animationDelay: `${animationDelay}ms` }}
                className={`bri-ProductCard bri-ZoomIn ${
                    isSelected ? 'bri-selected' : ''
                }`}
            >
                <div
                    className={`bri-ProductCard-body ${
                        isComparisonFull ? 'bri-comparing-full' : ''
                    }`}
                    onClick={() => {
                        if (isComparingMode) {
                            return onAction('compare');
                        }
                        dispatchEvent({
                            type: 'bri.click_product_card',
                            isInteraction: true,
                            ...(source === 'brilliant'
                                ? { source, brilliantSource: 'ui' }
                                : { source }),
                            productId,
                            featureType
                        });
                        return onAction('product-detail');
                    }}
                >
                    <div className={`bri-ProductCard-badges-container`}>
                        {!isComparingMode &&
                            discountBadge !== 'hidden' &&
                            discountedPercentage && (
                                <ProductDiscountBadge
                                    mode={discountBadge}
                                    percentage={discountedPercentage}
                                />
                            )}
                        {!isComparingMode &&
                            !discountedPercentage &&
                            !product.available && <ProductOutOfStockBadge />}
                    </div>
                    <ProductImageCarousel
                        product={product}
                        disableInteraction={isSelected}
                    >
                        <ProductCardOverlay
                            isVisible={isSelected}
                            iconId="check"
                            text={selectedOverlayText}
                        />
                        {!hideActions && (
                            <ProductCardButtons
                                isComparisonFull={isComparisonFull}
                                isComparingMode={isComparingMode}
                                isSelectedForComparison={
                                    isSelectedForComparison
                                }
                                onAction={onAction}
                            />
                        )}
                    </ProductImageCarousel>
                    <ProductRating
                        ratingCount={product.ratingCount}
                        ratingValue={product.ratingValue}
                    />
                    <div className={`bri-ProductCard-name bri-BodyFont`}>
                        {name}
                    </div>
                    <div className={`bri-ProductCard-footer bri-BodyFont`}>
                        <ProductPrice product={product} />
                        {!hideAddToCart && (
                            <IconButton
                                title={t('ADD_TO_CART_BUTTON_LABEL_ONE')}
                                variant="primary"
                                iconId="cart-plus"
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    return onAction('add-to-cart');
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

export default ProductCard;
